import { ChevronRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface MoreReportsListProps {
  entries: Array<{
    to: string
    /** Router search params, so a link carries the dashboard's current slice. */
    search?: Record<string, unknown>
    label: string
    badge?: string
  }>
}

/**
 * The secondary sections as a link list (CHG-2026-09-manager-reporting).
 *
 * On a phone the dashboard has room for the questions the owner asks first and no more; everything
 * below that becomes one tap away instead of a wall of stacked cards. Rows are 48px so they clear
 * the 44px touch target with room for a mis-tap.
 */
export function MoreReportsList({ entries }: MoreReportsListProps) {
  const { t } = useTranslation('reports')
  return (
    <Card className="lg:hidden">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          {t('more.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <ul className="divide-y divide-border">
          {entries.map((entry) => (
            <li key={entry.label}>
              <Link
                to={entry.to}
                search={entry.search}
                className="flex h-12 items-center gap-2 px-6 text-sm hover:bg-muted/50"
              >
                <span className="flex-1 truncate">{entry.label}</span>
                {entry.badge ? (
                  <Badge variant="secondary">{entry.badge}</Badge>
                ) : null}
                <ChevronRight
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
